import { useState } from 'react';
import { EyeOff, Plus, SmilePlus } from 'lucide-react';
import { COMMENT_MAX_LENGTH } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { IconButton } from '../ui.js';

const QUICK_EMOJI = ['🙂', '🎉', '🙏', '🔥', '🐛', '⚠️', '💡', '🚀', '❤️', '😅', '📈', '🤝'];

export function CommentComposer({
  columnId,
  disabled,
  disabledReason,
}: {
  columnId: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const send = useRetroStore((state) => state.send);
  const [text, setText] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit() {
    const value = text.trim();
    if (!value || saving) return;

    setSaving(true);
    const result = await send('comment:create', { columnId, text: value, isAnonymous: anonymous });
    setSaving(false);

    if (result !== null) {
      setText('');
      setShowEmoji(false);
    }
  }

  if (disabled) {
    return (
      <div className="hidden-count">
        <EyeOff size={15} aria-hidden />
        {disabledReason ?? 'Adding comments is closed.'}
      </div>
    );
  }

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
        onKeyDown={(event) => {
          // Enter submits; Shift+Enter is a newline. Mirrors chat conventions.
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void submit();
          }
        }}
        rows={3}
        placeholder="One idea per card…"
        aria-label="Add your idea"
        className="input resize-none"
      />

      {showEmoji ? (
        <div className="flex flex-wrap gap-1">
          {QUICK_EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="rounded-md px-1.5 py-1 text-base hover:bg-black/5"
              onClick={() => setText((current) => `${current}${emoji}`)}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}

      <div className="composer-row">
        <label className="anon">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(event) => setAnonymous(event.target.checked)}
          />
          Post anonymously
        </label>
        <span className="flex items-center gap-1">
          <IconButton
            label="Insert emoji"
            onClick={() => setShowEmoji((value) => !value)}
            className="!h-8 !w-8"
          >
            <SmilePlus size={16} />
          </IconButton>
          <button
            type="submit"
            disabled={!text.trim() || saving}
            className="btn btn-primary btn-sm"
          >
            <Plus size={14} aria-hidden /> Add card
          </button>
        </span>
      </div>

      {text.length > COMMENT_MAX_LENGTH - 100 ? (
        <p className="px-1 text-[11px] text-muted">
          {COMMENT_MAX_LENGTH - text.length} characters left
        </p>
      ) : null}
    </form>
  );
}
